<template>
  <div>
    <v-dialog
      persistent
      v-model="showClinicDialog"
      min-width="500"
      max-width="700"
      @keydown.esc="onCloseClinicDialog"
    >
      <template>
        <droopy-clinic-maintainer
          :clinic="selectedClinic"
          @close-dialog="onCloseClinicDialog"
        ></droopy-clinic-maintainer>
      </template>
    </v-dialog>

    <v-dialog v-model="confirmDisable" max-width="420">
      <v-card>
        <v-card-title class="text-h6">
          {{ disableTarget && isClinicActive(disableTarget) ? 'Deshabilitar clínica' : 'Habilitar clínica' }}
        </v-card-title>
        <v-card-text>
          <template v-if="disableTarget && isClinicActive(disableTarget)">
            Se deshabilitará la cuenta de administrador de
            <strong>{{ disableTarget.clinicName }}</strong> y dejará de poder operar el panel.
          </template>
          <template v-else>
            Se habilitará nuevamente la cuenta de administrador de
            <strong>{{ disableTarget && disableTarget.clinicName }}</strong>.
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDisable = false">Cancelar</v-btn>
          <v-btn color="primary" text @click="confirmDisableAction">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmResendTempPassword" max-width="420">
      <v-card>
        <v-card-title class="text-h6">Reenviar contraseña temporal</v-card-title>
        <v-card-text>
          Se generará una nueva contraseña temporal para el administrador de
          <strong>{{ resendTempPasswordTarget && resendTempPasswordTarget.clinicName }}</strong>
          y se le reenviará el correo de bienvenida {{ resendRecipient(resendTempPasswordTarget) }}.
          La contraseña temporal anterior dejará de funcionar.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmResendTempPassword = false">Cancelar</v-btn>
          <v-btn color="primary" text @click="confirmResendTempPasswordAction">Reenviar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de sospecha (019-mark-suspicious-entities-ui) -->
    <SuspiciousFlagModal
      v-model="showSuspiciousModal"
      :rut="suspiciousTarget && suspiciousTarget.clinicRut"
      entity-type="clinic"
      :entity-label="(suspiciousTarget && suspiciousTarget.clinicName) || 'esta clínica'"
      @saved="onSuspiciousSaved"
    />

    <v-container fluid>
      <v-row class="ma-0 my-4 align-center justify-space-between">
        <h1 class="text-h5 font-weight-bold">Droopy</h1>
        <v-btn color="primary" class="square-btn" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>Agregar clínica
        </v-btn>
      </v-row>
      <v-card outlined>
        <v-data-table
          :headers="headers"
          :items="clinics"
          item-key="clinicId"
          :loading="loadingClinics"
          no-data-text="Todavía no hay clínicas Droopy configuradas."
          :footer-props="{
            'items-per-page-options': [10, 20, 30, 40, 50]
          }"
        >
          <template v-slot:item.status="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" :color="isClinicActive(item) ? 'success' : 'grey'">
                  {{ isClinicActive(item) ? 'mdi-check-circle' : 'mdi-close-circle' }}
                </v-icon>
              </template>
              <span>{{ isClinicActive(item) ? 'Activa' : 'Inactiva' }}</span>
            </v-tooltip>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="d-flex align-center justify-end actions-cell">
              <user-actions
                :user="item"
                :show-actions="['edit', 'suspicious']"
                @edit="openEditDialog"
                @suspicious="openSuspicious"
              ></user-actions>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    fab
                    depressed
                    outlined
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    color="secondary"
                    :loading="resendingTempPasswordId === item.clinicId"
                    :disabled="resendingTempPasswordId === item.clinicId"
                    @click="askResendTempPassword(item)"
                  >
                    <v-icon small>mdi-lock-reset</v-icon>
                  </v-btn>
                </template>
                <span>Reenviar contraseña temporal</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    fab
                    dark
                    depressed
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                    @click="openServicesScreen(item)"
                  >
                    <v-icon small dark>mdi-medical-bag</v-icon>
                  </v-btn>
                </template>
                <span>Ver servicios</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    fab
                    dark
                    depressed
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    :color="isClinicActive(item) ? 'error' : 'success'"
                    :loading="togglingId === item.clinicId"
                    :disabled="!!togglingId"
                    @click="askDisable(item)"
                  >
                    <v-icon small dark>{{ isClinicActive(item) ? 'mdi-account-cancel' : 'mdi-account-check' }}</v-icon>
                  </v-btn>
                </template>
                <span>{{ isClinicActive(item) ? 'Deshabilitar' : 'Habilitar' }}</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import ClinicsService from '../services/clinics.service';
import DroopyClinicMaintainer from '../components/DroopyClinicMaintainer.vue';
import UserActions from '../components/UserActions.vue';
import SuspiciousFlagModal from '../components/SuspiciousFlagModal.vue';
import { utilsMixin } from '../mixins/utilsMixin';

// Códigos de rechazo del contrato 085 de pawer-backend (reset-temporary-password.md). Lo que
// no esté acá (INVALID_CLINIC_ID, 500, errores de red) cae en el mensaje genérico.
const RESEND_TEMP_PASSWORD_MESSAGES = {
  CLINIC_ADMIN_ALREADY_ACTIVATED: {
    tone: 'warning',
    text: 'El administrador ya activó su cuenta; esta acción es solo para contraseñas temporales vencidas. Si olvidó su contraseña, puede recuperarla desde el login de Droopy con «¿Olvidaste tu contraseña?».'
  },
  CLINIC_ADMIN_NOT_FOUND: {
    tone: 'error',
    text: 'Esta clínica no tiene una cuenta de administrador.'
  },
  CLINIC_ADMIN_NOT_ACTIVE: {
    tone: 'error',
    text: 'La cuenta de administrador de esta clínica está inactiva. Habilítala antes de reenviar la contraseña.'
  },
  CLINIC_ADMIN_EMAIL_MISSING: {
    tone: 'error',
    text: 'La cuenta de administrador no tiene un email registrado, así que no hay dónde enviar la contraseña.'
  },
  CLINIC_ADMIN_ACCOUNT_MISSING: {
    tone: 'error',
    text: 'La cuenta de administrador no tiene un usuario de acceso asociado. Contacta al equipo técnico.'
  },
  CLINIC_ADMIN_ACCOUNT_DISABLED: {
    tone: 'error',
    text: 'El acceso del administrador de esta clínica está deshabilitado. Contacta al equipo técnico.'
  }
};

const RESEND_TEMP_PASSWORD_GENERIC_ERROR = 'Ocurrió un error al reenviar la contraseña temporal. Intenta nuevamente.';

export default {
  name: 'DroopyClinics',

  components: { DroopyClinicMaintainer, UserActions, SuspiciousFlagModal },

  mixins: [utilsMixin],

  data() {
    return {
      clinics: [],
      loadingClinics: false,
      headers: [
        { text: 'Estado', value: 'status', sortable: false },
        { text: 'Nombre', value: 'clinicName' },
        { text: 'Email', value: 'email' },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ],
      showClinicDialog: false,
      selectedClinic: null,
      confirmDisable: false,
      disableTarget: null,
      togglingId: null,
      confirmResendTempPassword: false,
      resendTempPasswordTarget: null,
      resendingTempPasswordId: null,
      showSuspiciousModal: false,
      suspiciousTarget: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },

  mounted() {
    this.fetchClinics();
  },

  methods: {
    async fetchClinics() {
      this.loadingClinics = true;
      const res = await ClinicsService.getDroopyClinics();
      if (res.status === 'ok') {
        this.clinics = res.data;
      } else {
        this.showErrorSnackbar('No se pudieron cargar las clínicas Droopy.');
      }
      this.loadingClinics = false;
    },

    openCreateDialog() {
      this.selectedClinic = null;
      this.showClinicDialog = true;
    },

    openEditDialog(clinic) {
      this.selectedClinic = clinic;
      this.showClinicDialog = true;
    },

    onCloseClinicDialog() {
      this.showClinicDialog = false;
      this.selectedClinic = null;
      this.fetchClinics();
    },

    isClinicActive(item) {
      return item.status === 'active';
    },

    openServicesScreen(item) {
      this.$router.push({
        path: `/clinics/droopy/${item.clinicId}/services`,
        query: { clinicName: item.clinicName }
      });
    },

    askDisable(item) {
      this.disableTarget = item;
      this.confirmDisable = true;
    },

    async confirmDisableAction() {
      const target = this.disableTarget;
      this.confirmDisable = false;
      if (!target) return;

      this.togglingId = target.clinicId;
      const res = await ClinicsService.toggleClinicStatus(target.clinicId);
      if (res.status === 'ok') {
        target.status = this.isClinicActive(target) ? 'inactive' : 'active';
      } else {
        this.showErrorSnackbar('Ocurrió un error al cambiar el estado de la clínica. Intenta nuevamente.');
      }
      this.togglingId = null;
    },

    askResendTempPassword(item) {
      this.resendTempPasswordTarget = item;
      this.confirmResendTempPassword = true;
    },

    async confirmResendTempPasswordAction() {
      const target = this.resendTempPasswordTarget;
      this.confirmResendTempPassword = false;
      if (!target) return;

      this.resendingTempPasswordId = target.clinicId;
      const res = await ClinicsService.resendClinicAdminTempPassword(target.clinicId);
      this.notifyResendResult(res, target);
      this.resendingTempPasswordId = null;
    },

    notifyResendResult(res, target) {
      if (res.status === 'ok') {
        // La contraseña ya se regeneró aunque el correo falle; reintentar genera otra.
        if (res.data && res.data.emailSent === true) {
          this.showSuccessSnackbar(`Se envió una nueva contraseña temporal ${this.resendRecipient(target)}.`);
        } else {
          this.showWarningSnackbar('Se generó una nueva contraseña temporal, pero no se pudo enviar el correo. Intenta nuevamente.');
        }
        return;
      }
      const known = RESEND_TEMP_PASSWORD_MESSAGES[res.message];
      if (known && known.tone === 'warning') {
        this.showWarningSnackbar(known.text);
      } else {
        this.showErrorSnackbar(known ? known.text : RESEND_TEMP_PASSWORD_GENERIC_ERROR);
      }
    },

    resendRecipient(target) {
      return target && target.email ? `a ${target.email}` : 'al email de la cuenta';
    },

    openSuspicious(item) {
      this.suspiciousTarget = item;
      this.showSuspiciousModal = true;
    },

    onSuspiciousSaved() {
      this.showSuccessSnackbar('Sospecha actualizada correctamente.');
    }
  }
};
</script>

<style scoped>
.square-btn {
  border-radius: 8px !important;
}

.actions-cell {
  gap: 8px;
}
</style>
